import React from 'react';
import { useAuth } from '../../AuthProvider';
import BrowseContextMenu from './BrowseContextMenu';
import BrowseChrome from './components/BrowseChrome';
import BrowseListing from './components/BrowseListing';
import { useBrowseActions } from './hooks/useBrowseActions';
import { useBrowseItems } from './hooks/useBrowseItems';
import { useBrowsePrefs } from './hooks/useBrowsePrefs';
import { ICON_SIZE } from './utils/browseUtils';
import './styles/index.css';

/**
 * Browse — Explorer / Finder listing of one folder level.
 */
const BrowseView = ({
  organization = null,
  docs = [],
  folderId = null,
  onNavigateFolder,
  onOpenDoc,
  onOpenDocInNewTab,
  onCreateDoc,
  createDoc,
  showMissionNotes = true,
  variant = 'page',
  headerEnd = null,
}) => {
  const { role } = useAuth();
  const canViewAdminDocs = (role ?? 'admin') !== 'user';
  const { prefs, setPref, toggleSort } = useBrowsePrefs();
  const { path, folderItems, docItems } = useBrowseItems({
    organization,
    docs,
    folderId,
    canViewAdminDocs,
    showMissionNotes,
    sort: prefs.sort,
    dir: prefs.dir,
    onNavigateFolder,
  });
  const {
    dropTargetId,
    setDropTargetId,
    menu,
    setMenu,
    pasting,
    copied,
    handleCreateFolder,
    handleCreateDoc,
    handleCopyDoc,
    handlePasteDoc,
    onDragOver,
    onDropFolder,
    openItem,
    openItemInNewTab,
    openMenu,
  } = useBrowseActions({
    organization,
    folderId,
    onCreateDoc,
    createDoc,
    onNavigateFolder,
    onOpenDoc,
    onOpenDocInNewTab,
  });

  const iconSize = ICON_SIZE[prefs.view] || 16;
  const view = prefs.view;

  return (
    <div className={`docs-browse docs-browse--${view}${variant === 'popup' ? ' docs-browse--popup' : ''}`}>
      <BrowseChrome
        folderId={folderId}
        path={path}
        headerEnd={headerEnd}
        view={view}
        prefs={prefs}
        onNavigateFolder={onNavigateFolder}
        onCreateFolder={handleCreateFolder}
        onCreateDoc={handleCreateDoc}
        onSetPref={setPref}
      />

      <BrowseListing
        view={view}
        prefs={prefs}
        folderId={folderId}
        folderItems={folderItems}
        docItems={docItems}
        dropTargetId={dropTargetId}
        iconSize={iconSize}
        copied={copied}
        onToggleSort={toggleSort}
        onOpenItem={openItem}
        onOpenItemInNewTab={openItemInNewTab}
        onOpenMenu={openMenu}
        onDragOver={onDragOver}
        onDropFolder={onDropFolder}
        onDragLeave={() => setDropTargetId(null)}
      />

      <div className="docs-browse__status">
        {folderItems.length} folder{folderItems.length === 1 ? '' : 's'}
        {' · '}
        {docItems.length} doc{docItems.length === 1 ? '' : 's'}
      </div>

      <BrowseContextMenu
        menu={menu}
        copied={copied}
        pasting={pasting}
        onCopy={handleCopyDoc}
        onPaste={handlePasteDoc}
        onOpenInNewTab={openItemInNewTab}
        onClose={() => setMenu(null)}
      />
    </div>
  );
};

export default BrowseView;
